import * as React from 'react';
// E1 entitlement (2026-06-10) — Workspaces as a subscription entitlement.
// BILL-20a (D10): the decision is now the SERVER's. It reads
// `capabilities.workspaces` on GET /api/v1/billing/entitlement — computed by
// the same FeatureGate, over the same best-of gate tier, that refuses
// POST /api/v2/workspaces with 241 (BILL-20a-1 KD-S5) — composed with the
// `workspaces` deployment flag (useFeature over /api/v1/features). So an
// Enterprise-org member whose own org is Minimum sees create, exactly as the
// server lets them create. An absent map (pre-20a backend, demo guest, fetch
// never succeeded) is UNKNOWN and never entitles. The BE stays the real,
// fail-closed boundary; this module only decides UI visibility.
//
// Gating matrix (the E1 grandfathering design):
//   entitled                       → full UI (nav, routes, switcher, create)
//   not entitled + has workspaces  → collaborate-only UI (no create affordances)
//   not entitled + none            → no workspace chrome at all
import { getFeature, useFeatureFlagsStore } from '@/lib/featureFlags';
// Direct, not the barrel (D-P8 exception): this module sits in the settings
// registry's nav-rail predicate graph, which must stay cheap.
import { getCapabilityEntitlement, useCapability } from '@/features/billing/useCapability';
import { useEntitlementStore } from '@/features/billing/entitlementStore';
import { useSubscriptionStore } from '@/features/settings/subscriptionStore';

import { useWorkspaceStore } from './workspaceStore';

/**
 * Reactive: whether the current user may CREATE workspaces — the deployment
 * flag AND the server's capability verdict (unknown ⇒ no). Gates every create
 * affordance (New workspace buttons, CreateWorkspaceDialog).
 */
export function useWorkspacesEntitled(): boolean {
  const cap = useCapability('workspaces');
  return cap.released && cap.entitlement === 'entitled';
}

/** Non-reactive {@link useWorkspacesEntitled} for code outside React render. */
export function getWorkspacesEntitled(): boolean {
  return getFeature('workspaces') && getCapabilityEntitlement('workspaces') === 'entitled';
}

/**
 * Non-reactive visibility (entitled OR member/owner of ≥1 workspace — the
 * grandfather/grantee case). Used by the settings registry predicate, which
 * runs in the shared nav-rail render and must stay cheap and throw-free.
 */
export function getWorkspacesVisible(): boolean {
  return getWorkspacesEntitled() || useWorkspaceStore.getState().workspaces.length > 0;
}

export interface WorkspacesAccess {
  /** May create new workspaces (flag AND the server's capability verdict). */
  entitled: boolean;
  /** Owns or was granted ≥1 workspace (grandfather/grantee). */
  hasWorkspaces: boolean;
  /** Show workspace chrome at all: entitled OR hasWorkspaces. */
  visible: boolean;
  /**
   * True once `visible` can be trusted: every input that could still flip it
   * (feature flags, the entitlement verdict, the workspace list) has settled
   * to success or error. Routes use this to render a loading shell instead of
   * 404-ing a grandfathered member before their list loads — the "no
   * flash-then-hide" loading-guard convention.
   */
  resolved: boolean;
}

/**
 * Reactive workspace-access matrix for nav, routes, and the WorkspaceSwitcher.
 * Inputs are loaded once per session by AppConnection's auth-success handler
 * (see ensureWorkspaceEntitlementLoaded) — consumers only read.
 */
export function useWorkspacesAccess(): WorkspacesAccess {
  const flagsState = useFeatureFlagsStore((s) => s.loadState);
  const cap = useCapability('workspaces');
  const verdictStatus = useEntitlementStore((s) => s.status);
  const workspaceCount = useWorkspaceStore((s) => s.workspaces.length);
  const listState = useWorkspaceStore((s) => s.loadState);

  const flagOn = cap.released;
  const entitled = flagOn && cap.entitlement === 'entitled';
  const hasWorkspaces = workspaceCount > 0;
  const visible = entitled || hasWorkspaces;

  const flagsSettled = flagsState === 'ready' || flagsState === 'error';
  const verdictSettled = verdictStatus === 'ready' || verdictStatus === 'error';
  const listSettled = listState === 'ready' || listState === 'error';
  // Flag off (and flags settled) decides entitlement without the verdict.
  const entitlementSettled = flagsSettled && (!flagOn || verdictSettled);
  const resolved = visible || (entitlementSettled && listSettled);

  // Memoized on the primitive inputs — a fresh object per render is the same
  // bug class as the unstable-empty-array selectors (React #185 / memo busting).
  return React.useMemo(
    () => ({ entitled, hasWorkspaces, visible, resolved }),
    [entitled, hasWorkspaces, visible, resolved],
  );
}

/**
 * Kick off every load the access matrix depends on. Called from
 * AppConnection's authenticationSuccessResponse handler (auth is confirmed
 * there, so both the WS subscription request and the JWT-bearing HTTP
 * fetches are valid). All loads are guarded, so reconnects are cheap; the
 * dashboard layout refreshes the verdict on tierChangedEvent.
 */
export function ensureWorkspaceEntitlementLoaded(): void {
  // The capability verdict (GET /billing/entitlement). From idle only: load()
  // on a ready store is a refresh, i.e. one more GET per reconnect.
  const ent = useEntitlementStore.getState();
  if (ent.status === 'idle') void ent.actions.load();
  // Subscription state is NOT a workspace input any more, but this is the
  // app's only startup load of it (subscriptionStore.tierName — the display
  // tier, and the Docling lane fallback a demo guest relies on, D-P3). Keep it.
  const sub = useSubscriptionStore.getState();
  if (sub.loadState.status === 'idle') {
    sub.actions.load();
  }
  const ws = useWorkspaceStore.getState();
  if (ws.loadState === 'idle') {
    void ws.actions.loadWorkspaces();
  }
}
